import { CATALOGS, type Language, type MessageKey } from './messages'

const MESSAGE_TOKEN_PATTERN = /\{(\w+)\}/g

export type { Language, MessageKey } from './messages'

/**
 * The language each organization's widget chrome speaks, as a BCP 47 locale.
 * Absent means English. Posts, branding copy and surface titles are the
 * organization's own words and are never translated.
 */
export const ORGANIZATION_LOCALES: Readonly<Record<string, string>> = Object.freeze({
  rushbet: 'es-419',
})

export type MessageParams = Readonly<Record<string, string | number>>
type PluralBase = { [K in MessageKey]: K extends `${infer Base}.one` ? Base : never }[MessageKey]

export interface Translator {
  language: Language
  /**
   * Locale for numbers, money and dates. The host's own locale when it speaks
   * the chrome's language (es-MX, es-PE), so amounts read the way the
   * sportsbook prints them; otherwise the organization's.
   */
  locale: string | undefined
  t: (key: MessageKey, params?: MessageParams) => string
  tCount: (key: PluralBase, count: number, params?: MessageParams) => string
}

export function organizationLocale(organization: string | null | undefined): string | undefined {
  return organization ? ORGANIZATION_LOCALES[organization.trim().toLowerCase()] : undefined
}

function languageOf(locale: string | undefined): Language {
  const language = locale?.split('-')[0]?.toLowerCase()
  return language && language in CATALOGS ? (language as Language) : 'en'
}

function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template
  return template.replace(MESSAGE_TOKEN_PATTERN, (match, name: string) => (
    name in params ? String(params[name]) : match
  ))
}

export function createTranslator(organization?: string | null, hostLocale?: string): Translator {
  const orgLocale = organizationLocale(organization)
  const language = languageOf(orgLocale)
  const hostLanguage = hostLocale?.split('-')[0].toLowerCase()
  const locale = hostLocale && hostLanguage === language ? hostLocale : orgLocale ?? hostLocale
  const catalog = CATALOGS[language]
  const plural = new Intl.PluralRules(locale ?? language)
  const t = (key: MessageKey, params?: MessageParams) => interpolate(catalog[key], params)
  const tCount = (key: PluralBase, count: number, params?: MessageParams) => {
    const form = plural.select(count) === 'one' ? 'one' : 'other'
    return t(`${key}.${form}` as MessageKey, { count, ...params })
  }
  return { language, locale, t, tCount }
}

/** English, for code that renders outside any organization. */
export const DEFAULT_TRANSLATOR = createTranslator()
